import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import { GeoLocationStatus, type ILocationData } from '@endeavour/enhanced-geo-integration';

import { isInApp } from '../../main/util/is-app';
import { usePrevious } from '../../shared/hooks/use-previous';
import { useToast } from '../../shared/hooks/use-toast';
import { useGeolocationStatus } from '../util/geolocation-context';

function createToast(location: ILocationData, toast: ReturnType<typeof useToast>) {
    switch (location.status) {
        case GeoLocationStatus.ALLOWED:
            return toast.success(() => <FormattedMessage id="Geolocation request successful" />);
        case GeoLocationStatus.PENDING:
            return toast.loading(() => <FormattedMessage id="Loading GeoLocation" />);
        case GeoLocationStatus.DENIED:
            return toast.error(() => <FormattedMessage id="GeoLocation Denied" />);
    }
}

export function useGeoLocationToast() {
    const toast = useToast();
    const auth = useAuth();
    const status = useGeolocationStatus();
    const latestToastId = useRef<string | undefined>();
    const previousStatus = usePrevious(status.status);
    const isNativeApp = isInApp();
    const notify = useCallback(
        (location: ILocationData) => {
            if (auth.isLoading || !auth.isAuthenticated || isNativeApp) {
                return;
            }

            if (latestToastId.current) {
                toast.dismiss(latestToastId.current);
            }
            latestToastId.current = createToast(location, toast);
        },
        [toast, auth.isLoading, auth.isAuthenticated],
    );

    useEffect(() => {
        if (previousStatus !== status.status || !latestToastId.current) {
            notify(status);
        }
    }, [status, previousStatus, notify]);
}
